<template>
    <article class="notification-alert-card" :aria-label="content.title" aria-atomic="true">
        <button
            type="button"
            class="notification-alert-open"
            :aria-label="$t('Open notification') + ': ' + content.title"
            :aria-describedby="'notification-alert-message-' + notification.id"
            @click="$emit('open')"
        ></button>
        <div class="notification-alert-icon" :class="'accent-' + content.accent" aria-hidden="true">
            <i :class="content.icon"></i>
        </div>
        <div class="notification-alert-content">
            <p class="notification-alert-title">{{ content.title }}</p>
            <p :id="'notification-alert-message-' + notification.id" class="notification-alert-message">{{ message }}</p>
        </div>
        <ActionIconButton
            class="notification-alert-dismiss"
            icon="fas fa-xmark"
            size="xs"
            style-type="neutral-transparent"
            :aria-label="$t('Dismiss notification')"
            @click="$emit('dismiss')"
        />
    </article>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import type { NotificationItem } from "@/api/definitions";
import { getNotificationContent, getNotificationMessage } from "@/utils/notification-content";
import ActionIconButton from "@/components/buttons/ActionIconButton.vue";

export default defineComponent({
    components: { ActionIconButton },
    name: "NotificationAlertCard",
    emits: ["open", "dismiss"],
    props: {
        notification: { type: Object as PropType<NotificationItem>, required: true },
    },
    computed: {
        content() {
            return getNotificationContent(this.notification.type, this.$t);
        },
        message(): string {
            return getNotificationMessage(this.notification, this.$t);
        },
    },
});
</script>

<style scoped>
.notification-alert-card {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: var(--space-2);
    padding: var(--space-3);
    border: var(--border-width-base) solid var(--color-border-default);
    border-radius: var(--rounded-lg);
    background: var(--color-background-surface);
    color: var(--color-text-default);
    box-shadow: var(--box-shadow);
    transition: background-color 120ms ease;
}
.notification-alert-card:hover,
.notification-alert-card:focus-within {
    background: var(--color-background-neutral-hover-subtle);
}
.notification-alert-open {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    padding: 0;
    border: 0;
    border-radius: inherit;
    background: transparent;
    cursor: pointer;
}
.notification-alert-dismiss {
    position: relative;
    z-index: 1;
    flex-shrink: 0;
}
.notification-alert-card :deep(button:focus-visible) {
    outline: 2px solid var(--color-text-primary-brand-default);
    outline-offset: -2px;
}
.notification-alert-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: var(--rounded-full);
    background: var(--color-background-neutral-subtler);
    color: var(--color-text-default);
}
.accent-success {
    background: var(--color-background-success-subtler);
    color: var(--color-icon-success);
}
.accent-info {
    background: var(--color-background-info-subtler);
    color: var(--color-chart-variant-one);
}
.accent-warning {
    background: var(--color-background-warning-subtler);
    color: var(--color-icon-warning);
}
.accent-danger {
    background: var(--color-background-danger-subtler);
    color: var(--color-icon-danger);
}
.notification-alert-content {
    flex: 1;
    min-width: 0;
    pointer-events: none;
}
.notification-alert-title,
.notification-alert-message {
    margin: 0;
    font-size: var(--text-sm);
    line-height: var(--leading-5);
    overflow-wrap: anywhere;
}
.notification-alert-title {
    margin-bottom: var(--space-1);
    font-weight: 600;
}
.notification-alert-message {
    font-size: var(--text-xs);
    line-height: var(--leading-4);
    color: var(--color-text-neutral-on-neutral-bg);
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
}
</style>
